import type { Passkey } from "@esi/shared";
import { KeyRound } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "../i18n/format.ts";
import { webAuthnSupported } from "../shell/webauthn.ts";
import { RemovePasskeyDialog } from "./RemovePasskeyDialog.tsx";

/**
 * The passkeys block shared by the tenant account page and the console profile: the list
 * (shown without a step-up from ESI's copy, #389; behind a step-up only while `passkeys` is null,
 * a list ESI has not read yet), "Add a passkey" (or, after a step-up,
 * "Add the passkey", #354), the error and the confirmation line. A removal asks first in a
 * dialog naming the passkey. It calls no API: each page passes its own actions.
 */
export function PasskeysPanel(props: {
  heading: ReactNode;
  lead: ReactNode;
  /** Null while ESI has no copy of the list yet: a step-up reads it. */
  passkeys: Passkey[] | null;
  busy: boolean;
  error: string | null;
  status: string | null;
  /**
   * A step-up just confirmed an add: the browser's ceremony needs a fresh click, so the add
   * button reads "Add the passkey" (`onAdd` then starts the ceremony).
   */
  addReady?: boolean;
  onReveal: () => void;
  onAdd: () => void;
  onRemove: (passkey: Passkey) => void;
}) {
  const { t } = useTranslation();
  const supported = webAuthnSupported();
  const { passkeys } = props;
  // The passkey whose removal is being confirmed.
  const [removing, setRemoving] = useState<Passkey | null>(null);
  // Each row's Remove: focus is back on it before the removal runs (a step-up returns there).
  const removeButtons = useRef(new Map<string, HTMLButtonElement>());
  return (
    <div className="flex max-w-xl flex-col gap-3">
      {props.heading}
      <p className="text-sm leading-relaxed text-fg-secondary">{props.lead}</p>
      {!supported ? <Notice>{t("passkeys.unsupported")}</Notice> : null}
      {passkeys === null ? (
        <div>
          <Button type="button" variant="outline" disabled={props.busy} onClick={props.onReveal}>
            {t("passkeys.reveal")}
          </Button>
        </div>
      ) : passkeys.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("passkeys.none")}</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
          {passkeys.map((passkey) => (
            <li key={passkey.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <KeyRound aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm text-fg">{passkeyName(passkey, t)}</span>
                <span className="text-xs text-fg-muted">
                  {t("passkeys.created", { date: formatDateTime(passkey.createdAt) })}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={props.busy}
                ref={(el) => {
                  if (el) {
                    removeButtons.current.set(passkey.id, el);
                  } else {
                    removeButtons.current.delete(passkey.id);
                  }
                }}
                onClick={() => setRemoving(passkey)}
                aria-label={t("passkeys.removeNamed", { name: passkeyName(passkey, t) })}
              >
                {t("app.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {supported ? (
        <div className="flex flex-col items-start gap-2">
          {props.addReady ? (
            <p className="text-sm text-fg-secondary">{t("passkeys.addReady")}</p>
          ) : null}
          {/* One button in one place: focus coming back from the step-up lands on it. */}
          <Button type="button" disabled={props.busy} onClick={props.onAdd}>
            {props.addReady ? t("passkeys.addConfirmed") : t("passkeys.add")}
          </Button>
        </div>
      ) : null}
      {props.error ? <InlineError>{props.error}</InlineError> : null}
      <p role="status" className="text-sm text-success-fg">
        {props.status}
      </p>
      <RemovePasskeyDialog
        passkey={removing}
        name={removing ? passkeyName(removing, t) : ""}
        busy={props.busy}
        onClose={() => setRemoving(null)}
        onConfirm={(passkey) => {
          setRemoving(null);
          removeButtons.current.get(passkey.id)?.focus();
          props.onRemove(passkey);
        }}
      />
    </div>
  );
}

/** A passkey's name as the authenticator gave it, or a generic one. */
export function passkeyName(passkey: Passkey, t: (key: "passkeys.unnamed") => string): string {
  return passkey.name || t("passkeys.unnamed");
}
